import React, { Suspense } from "react";
import { useFormik } from "formik";
import { Input } from "@ifgengineering/component-library";
import { ThemedText } from "@components/ThemedText";
import Checkbox from "@components/DropDown/styled/Checkbox";
import { NavItemComponentType } from "@components/NavigationShell/NavigationShell.component";
import Cur8OnboardingLayout from "../Layout";
import { ContentItemWrapper, ContentWrapper } from "./styled";
import {
  accreditedInvestorDeclaration,
  highNetWorthInvestorDeclaration,
  selfCertifiedInvestorDeclaration,
} from "./declarations";
import { InvestorProfile } from "@ifgengineering/client-invest-sdk";
import { List } from "react-content-loader";
import { EligibleSchema } from "./validationSchema";

const ReactMarkdown = React.lazy(() => import("react-markdown"));

interface FormikState {
  investorProfileSigned: boolean;
  investorProfileSignedDate?: string;
  investorProfileEligibilityReason: string;
}

export const Eligible: React.FC<NavItemComponentType> = ({
  prevPage,
  nextPage,
  handlePageSubmit,
  contextActions,
}) => {
  const { getOneItem, updateStore } = contextActions;
  const storedInvestorProfileSigned = getOneItem("investorProfileSigned");
  const investorProfileEligibilityReason = getOneItem(
    "investorProfileEligibilityReason"
  );
  const storedInvestorProfileSignedDate = getOneItem(
    "investorProfileSignedDate"
  );

  const formik = useFormik<FormikState>({
    initialValues: {
      investorProfileSigned: storedInvestorProfileSigned || false,
      investorProfileSignedDate: storedInvestorProfileSignedDate,
      investorProfileEligibilityReason: investorProfileEligibilityReason || "",
    },
    validationSchema: EligibleSchema,
    onSubmit: async ({
      investorProfileSigned,
      investorProfileSignedDate,
      investorProfileEligibilityReason,
    }) => {
      await handlePageSubmit(
        {
          investorProfileSigned,
          investorProfileEligibilityReason,
          investorProfileSignedDate,
        },
        () => {
          updateStore({
            investorProfileSigned,
            investorProfileEligibilityReason,
            ...(investorProfileSignedDate ? { investorProfileSignedDate } : {}),
          });
          nextPage();
        }
      );
    },
  });

  const handleChange = () => {
    const investorProfileSignedDate = new Date().toISOString();

    formik.setFieldValue(
      "investorProfileSigned",
      !formik.values.investorProfileSigned
    );
    formik.setFieldValue(
      "investorProfileSignedDate",
      investorProfileSignedDate
    );
    updateStore({
      investorProfileSigned: !storedInvestorProfileSigned,
      investorProfileSignedDate,
      investorProfileEligibilityReason:
        formik.values.investorProfileEligibilityReason,
    });
  };

  const investorProfile: InvestorProfile = getOneItem("investorProfile");
  const firstName: string = getOneItem("firstName");
  const lastName: string = getOneItem("lastName");
  const fullName = `${firstName} ${lastName}`;
  const hasTheUserSigned = formik.values.investorProfileSigned;
  const signatureFormatedDate = formik.values.investorProfileSignedDate || "";

  return (
    <Cur8OnboardingLayout
      heading="Eligibility"
      subheading="Declaration"
      onClickBack={prevPage}
      onSubmit={formik.handleSubmit}
      disableNext={!hasTheUserSigned}
    >
      <ContentWrapper>
        <ContentItemWrapper>
          <Suspense fallback={<List />}>
            <ReactMarkdown className="cur8Markdown">
              {declarationMap[investorProfile]?.markdown}
            </ReactMarkdown>
          </Suspense>
        </ContentItemWrapper>
        <Input
          name="investorProfileEligibilityReason"
          testId="investor-profile-eligibility-Reason"
          value={formik.values.investorProfileEligibilityReason}
          onChange={(e) =>
            formik.setFieldValue(
              "investorProfileEligibilityReason",
              e.target.value
            )
          }
          heading="Briefly state why you meet the investor criteria you have selected"
          placeholder="e.g. by stating your annual salary, income, or the name of a private company, you have previously invested in, etc."
          isLong
          height="auto"
          errorMessage={
            formik.touched.investorProfileEligibilityReason
              ? formik?.errors?.investorProfileEligibilityReason
              : ""
          }
        />
        <Checkbox
          testId="sign-profile-checkbox"
          onChange={handleChange}
          checked={formik.values.investorProfileSigned}
          label={`I declare myself a ${declarationMap[investorProfile]?.text} Investor`}
          labelFontSize="H16"
          labelColor="SLATE900"
        />
        {hasTheUserSigned && (
          <Signature name={fullName} date={signatureFormatedDate} />
        )}
      </ContentWrapper>
    </Cur8OnboardingLayout>
  );
};

export const Signature = ({
  name,
  date,
}: {
  name: string;
  date?: string;
}): JSX.Element => (
  <div>
    <ThemedText type="H20" color="SLATE900">
      {name}
    </ThemedText>
    <ThemedText type="S16" color="SLATE700">
      Declaration signed on {date}
    </ThemedText>
  </div>
);

export const declarationMap: Record<
  InvestorProfile,
  { text: string; markdown: string }
> = {
  US_ACCREDITED: {
    text: "accredited",
    markdown: accreditedInvestorDeclaration,
  },

  SELF_CERTIFIED: {
    text: "self-certified sophisticated",
    markdown: selfCertifiedInvestorDeclaration,
  },
  HIGH_NET_WORTH: {
    text: "high net worth",
    markdown: highNetWorthInvestorDeclaration,
  },
  UNACCREDITED: {
    text: "none",
    markdown: "",
  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error investorProfile "NONE" to be removed
  NONE: {
    text: "none",
    markdown: "",
  },
};
